import type { AxiosRequestConfig, AxiosResponse } from 'axios'
import axios from 'axios'
import { ResultEnum } from '@/types/misc/http'
import { handleChangeRequestHeader, handleConfigureAuth, transformErrorResponse } from '@/axios/utils/tools'
import { handleTerminalAuthFailure, handleTokenExpired, isAuthRecoveryRequestUrl, isTerminalAuthFailureReason, throwErrorMessage } from '@/axios/utils/error'
import store from '@/store'
import { removeSessionStorage } from '@/utils/cache'
import { LOGIN_FAILED_RETRY_COUNT } from '@/constant/cache-key'

const instance = axios.create({
  baseURL: '/',
  timeout: 1e3 * 60,
  withCredentials: true, // 跨域时候允许携带凭证
  validateStatus: (status: number) => {
    return status >= 200 && status <= 500
  },
})
let controller = new AbortController()

function resetController() {
  controller = new AbortController()
}

interface AuthReplayRequestConfig extends AxiosRequestConfig {
  authReplayAttempted?: boolean
  skipRequestDedup?: boolean
  skipAuthRenewal?: boolean
}

function shouldReplayAfterRenewal(config?: AuthReplayRequestConfig) {
  if (!config?.url || config.authReplayAttempted || isAuthRecoveryRequestUrl(config.url)) {
    return false
  }
  const method = config.method?.toUpperCase() ?? 'GET'
  return method === 'GET' || method === 'HEAD'
}

function createReplayRequestConfig(config: AuthReplayRequestConfig) {
  return {
    ...config,
    authReplayAttempted: true,
    skipAuthRenewal: true,
    skipRequestDedup: true,
    signal: undefined,
  } satisfies AuthReplayRequestConfig
}

/**
 * 请求拦截器
 */
instance.interceptors.request.use(async (config) => {
  if (controller.signal.aborted) {
    resetController()
  }
  const signal = controller.signal
  config.signal = signal
  const requestConfig = config as AuthReplayRequestConfig
  if (!requestConfig.skipAuthRenewal && !isAuthRecoveryRequestUrl(config.url)) {
    const sessionReady = await store.authStore.ensureSession('request')
    const reason = store.authStore.lastSessionRenewalFailureReason
    if (!sessionReady && isTerminalAuthFailureReason(reason)) {
      await handleTerminalAuthFailure(reason)
      return Promise.reject(new Error(`Session renewal failed: ${reason}`))
    }
  }
  config = handleChangeRequestHeader(config)
  config = handleConfigureAuth(config)
  // config = handleRequestLog(config)
  return config
}, (error) => {
  return Promise.reject(error)
})

/**
 * 响应拦截器
 */
instance.interceptors.response.use(async (response: AxiosResponse) => {
  const { status: code, data } = response
  const status = data.error_code || code || data.code || data.return_code
  const authFailureReason = data?.reason ?? data?.data?.reason
  const isTerminalAuthFailure = isTerminalAuthFailureReason(authFailureReason)
  const disposition = response.headers['content-disposition'] // 获取Content-Disposition
  // const message = data.return_msg || i18n.global.t('common.request.unknownError')
  // handleResponseLog(response)
  const { setLoginStatus } = store.authStore
  const requestConfig = response.config as AuthReplayRequestConfig
  const isAuthRecoveryRequest = isAuthRecoveryRequestUrl(requestConfig.url)
  // 5001 表示当前用户无 STP 使用权限
  if (status === ResultEnum.UNAUTHORIZED || data?.data?.error_code === 5001 || (code === 403 && isTerminalAuthFailure)) {
    const renewed = await handleTokenExpired(response)

    if (renewed && shouldReplayAfterRenewal(requestConfig)) {
      return instance.request(createReplayRequestConfig(requestConfig))
    }

    if (!renewed && !isAuthRecoveryRequest) {
      setLoginStatus(3)
    }

    return Promise.reject(transformErrorResponse(response))
  }
  else {
    removeSessionStorage(LOGIN_FAILED_RETRY_COUNT)
  }
  if ((typeof status === 'number' && status !== 200) || (typeof data.return_code === 'string' && data.return_code === '0')) {
    return throwErrorMessage(response) // 这里是抛出错误的地方
  }
  return disposition // 当Content-Disposition中有值的时候进行处理，其他请求的响应则放过
    ? {
        ...response,
        data: {
          blob: data,
          fileName: decodeURI(disposition.split(';')[1].split('filename=')[1]), // 处理Content-Disposition，获取header中的文件名
        },
      }
    : response
}, async (error) => {
  return Promise.reject(error)
})

export default instance
